import React, { useState } from "react";
import { Box, Container, Fab, TableCell } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import { SearchHcpForm } from "./components";
import { CTable, ErrorBox, Text, HFlex, VFlex, CDialog, Loading } from "shared";
import { useSearchHcps, HcpSearchCategory as Category } from "api/hooks/useHCPs";
import CreateHcpForm from "routes/hcp/components/CreateHcpForm";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "utils/hooks";
import { Country, countries } from "utils/market";
import { Area, WithChildren } from "types";
import { RowLink } from "shared/CTable/CTable";
import { join } from "utils/misc";
import { HcpListItem, HcpListResponse } from "routes/hcp/types";

const Cell: React.VFC<WithChildren> = ({ children }) => (
  <TableCell>
    <Text tiny>{children}</Text>
  </TableCell>
);

const HcpSearch: React.VFC = () => {
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);

  const openCreateDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const searchQuery = useSearchParams(
    {
      country: countries[0].value,
      category: Category.id,
      search: "",
    },
    {
      country: Object.values(Country),
      category: Object.values(Category),
    }
  );

  const { data, error, isLoading, isLoadingError: isError, isSuccess, isIdle } = useSearchHcps(searchQuery);
  const response = data as HcpListResponse | undefined;

  const tableHeader = {
    id: t("patients.id"),
    name: t("form.name"),
  };

  const tableCellFactory = (item: HcpListItem) => ({
    id: (
      <Cell>
        <RowLink to={{ pathname: join(Area.Hcps, item.id), state: window.location.href.match(/\?.+$/)?.[0] }} />
        <Text tiny>{item.id}</Text>
      </Cell>
    ),
    name: <Cell>{item.name}</Cell>,
  });

  const noMatchEm = (
    <Text bold secondary paragraph mb={0} center>
      {t("dashboard.tables.no_hcps_found")}
    </Text>
  );

  const tableEm = (
    <Container>
      <CTable header={tableHeader} items={response?.items} cellFactory={tableCellFactory} />
    </Container>
  );

  const resultEm = (
    <Box bgcolor="background.paper" py={2} overflow="auto" data-testid="search-results">
      {isError && <ErrorBox error={error!} verbose centered dark />}
      {isLoading && <Loading />}
      {isSuccess && (response?.items?.length ? tableEm : noMatchEm)}
    </Box>
  );

  return (
    <VFlex>
      <HFlex alignSelf="flex-end" mr={3} my={3}>
        <Box display="inline-flex" alignItems="center">
          <Text caption secondary mr={1}>
            {t("buttons.create")}
          </Text>
          <Fab color="secondary" size="medium" onClick={openCreateDialog} aria-label="Create HCP">
            <Add />
          </Fab>
        </Box>
      </HFlex>
      <Container maxWidth="md">
        <Box mb={3}>
          <SearchHcpForm {...searchQuery} />
        </Box>
      </Container>
      {!isIdle && resultEm}

      <CDialog maxWidth="sm" fullWidth open={isDialogOpen} onClose={closeDialog}>
        <CreateHcpForm onSuccess={closeDialog} onCancel={closeDialog} />
      </CDialog>
    </VFlex>
  );
};

export default HcpSearch;
